import { useState } from "react";
import { Quote } from "../../components";
import { ArrowIcon } from "../../icons";
import { useAppContext } from "../../context";
import { createShopifyOrder } from "../../utils/create-shopify-order";
import { updateCustomerWithOrder } from "../../utils/update-customer-with-order";
import { getMonthlyLoanPayment } from "../../utils/get-monthly-loan-payment";
import { renderPrice } from "../../utils/render-price";
import { calculateLoan } from "../../utils/calculate-loan";
import toast, { Toaster } from "react-hot-toast";

export const SelectedOptionScreen = () => {
  const {
    selectedOption,
    setCurrentStep,
    shopifyCustomer,
    setShopifyOrder,
    setSelectedOption,
    isLoading: isGlobalLoading,
    typeformAnswers,
  } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isFinanceitLoading, setIsFinanceitLoading] = useState(false);

  const handleAddAddon = async (addon) => {
    setSelectedOption((prev) => ({
      ...prev,
      total: prev.total + addon.price,
      addons: prev.addons.map((prevAddon) =>
        prevAddon.option === addon.option
          ? { ...prevAddon, isSelected: true }
          : prevAddon
      ),
    }));
  };

  const handleRemoveAddon = (addon) => {
    setSelectedOption((prev) => ({
      ...prev,
      total: prev.total - addon.price,
      addons: prev.addons.map((prevAddon) =>
        prevAddon.option === addon.option
          ? { ...prevAddon, isSelected: false }
          : prevAddon
      ),
    }));
  };

  const handleCreateShopifyOrder = async ({ withFinanceit } = {}) => {
    const products = selectedOption.items
      .filter((item) => !item.readonly)
      .map((item) => ({
        title: item.title,
        price: item.price,
        netSuiteId: item.netsuite_id,
      }));

    const warranties = selectedOption.isWarrantySelected
      ? selectedOption.items.map((item) => ({
          title: `Extended Warranty: ${item.title}`,
          price: item.warranty_price,
        }))
      : [];

    const addons = selectedOption.addons
      .filter((addon) => addon.isSelected)
      .map((addon) => ({
        title: addon.title,
        price: addon.price,
        netSuiteId: addon.netsuite_id,
      }));

    try {
      const order = await createShopifyOrder({
        customerId: shopifyCustomer.id,
        products: [...products, ...warranties, ...addons],
        grantAllowance: selectedOption.grantAllowance,
        withFinanceit,
        monthlyFinancingAmount: getMonthlyLoanPayment(selectedOption.loans),
        financingRequestedAmount: selectedOption.total,
        postalCode:
          typeformAnswers.postal_code_q || typeformAnswers.postal_code,
        typeformAnswers: Object.entries(typeformAnswers).map(
          ([key, value]) => ({
            key,
            value,
          })
        ),
      });

      setShopifyOrder(order);
      
      // Build metafields payload from typeform answers and order data
      const tf = typeformAnswers || {};
      const metafields = {
        // Grant and financing
        grant_savings: selectedOption.grantAllowance || "",
        monthly_financing_amount: getMonthlyLoanPayment(selectedOption.loans) || "",
        // Postal code
        quiz_postal_code_q: tf.postal_code_q || tf.postal_code || "",
        // Owner type
        quiz_owner: tf.owner || "",
        // Home details
        quiz_home_type: tf.home_type || "",
        quiz_heat_source: tf.heat_source || "",
        quiz_heating_type: tf.heating_type || "",
        quiz_electrical_panel: tf.electric_panel || "",
        quiz_system_age: tf.system_age || "",
        quiz_air_conditioner:
          typeof tf.air_conditioner !== "undefined"
            ? String(tf.air_conditioner)
            : "",
        // Square footage
        quiz_sq_ft_central: tf["sq_ft-Central"] || tf.sq_ft || "",
        quiz_sq_ft_ductless: tf["sq_ft-Ductless"] || "",
        quiz_condenser_mounted: tf.condenser_mounted || "",
        quiz_room_number: tf.room_number || "",
      };

      // Add room size metafields dynamically based on room count
      if (tf.room_number === "2") {
        metafields["quiz_rooms_2_size_room_1"] = tf["rooms_2_size-room_1"] || "";
        metafields["quiz_rooms_2_size_room_2"] = tf["rooms_2_size-room_2"] || "";
      } else if (tf.room_number === "3") {
        metafields["quiz_rooms_3_size_room_1"] = tf["rooms_3_size-room_1"] || "";
        metafields["quiz_rooms_3_size_room_2"] = tf["rooms_3_size-room_2"] || "";
        metafields["quiz_rooms_3_size_room_3"] = tf["rooms_3_size-room_3"] || "";
      } else if (tf.room_number === "4") {
        metafields["quiz_rooms_4_size_room_1"] = tf["rooms_4_size-room_1"] || "";
        metafields["quiz_rooms_4_size_room_2"] = tf["rooms_4_size-room_2"] || "";
        metafields["quiz_rooms_4_size_room_3"] = tf["rooms_4_size-room_3"] || "";
        metafields["quiz_rooms_4_size_room_4"] = tf["rooms_4_size-room_4"] || "";
      }

      // Update customer with latest order details (also pass order metadata)
      await updateCustomerWithOrder(shopifyCustomer.id, {
        orderId: order.id,
        orderDate: new Date().toISOString(),
        products: selectedOption.items.filter((item) => !item.readonly),
        addons: selectedOption.addons.filter((addon) => addon.isSelected),
        grantAllowance: selectedOption.grantAllowance,
        totalPrice: selectedOption.total,
        withFinanceit,
        monthlyFinancingAmount: getMonthlyLoanPayment(selectedOption.loans),
        metafields,
      });
      
      return order;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleFinishWithSpecialist = async () => {
    setIsLoading(true);
    try {
      await handleCreateShopifyOrder();
      setCurrentStep("confirmation");
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinanceit = async () => {
    setIsFinanceitLoading(true);

    try {
      const { financeitInviteLink } = await handleCreateShopifyOrder({
        withFinanceit: true,
      });

      setIsFinanceitLoading(false);
      setCurrentStep("confirmation");
      window.open(financeitInviteLink, "_blank");
    } catch (error) {
      setIsFinanceitLoading(false);
      toast.error("Something went wrong");
    }
  };

  return (
    <div>
      <div>
        <h1 className="tw-text-4xl tw-text-blue tw-font-bold tw-my-12 tw-text-center">
          Your personalized quote
        </h1>
        <Quote
          option={selectedOption}
          shouldRenderSelectButton={false}
          onChange={(option) => setSelectedOption(option)}
        />
      </div>
      <div className="tw-mt-[75px]">
        <div className="tw-text-center tw-font-medium tw-text-3xl tw-text-blue">
          Add-on products
        </div>
        <div className="tw-mt-5 tw-flex tw-flex-col lg:tw-flex-row tw-justify-center lg:tw-items-center md:tw-p-10 tw-px-2 tw-py-10 tw-bg-white tw-gap-x-32 tw-gap-y-8 tw-border tw-border-[#ECF0F4] tw-rounded">
          {selectedOption.addons.map((addon) => (
            <div key={addon.id} className="tw-flex tw-items-center tw-gap-x-7 ">
              <div className="tw-p-3 tw-bg-[#F7F7F7] tw-rounded-md tw-border tw-border-[#EEEEEE] tw-w-[140px] tw-h-[140px]">
                <img src={addon.image} className="tw-object-contain" />
              </div>
              <div className="tw-gap-y-3">
                <div className="tw-font-semibold tw-text-blue">
                  {addon.title}
                </div>
                <div className="tw-font-medium tw-text-[#00304A99] tw-text-[15px]">
                  {renderPrice(addon.price)}
                </div>
                <button
                  disabled={isGlobalLoading}
                  className={`tw-rounded-full tw-inline-flex tw-items-center tw-justify-center tw-h-10 md:tw-h-[50px] tw-border tw-border-orange tw-px-10 tw-text-orange tw-font-semibold tw-mt-2 ${
                    addon.isSelected ? "tw-bg-orange tw-text-white" : ""
                  } ${
                    isGlobalLoading ? "tw-opacity-70 tw-cursor-not-allowed" : ""
                  }`}
                  onClick={() => {
                    addon.isSelected
                      ? handleRemoveAddon(addon)
                      : handleAddAddon(addon);
                  }}
                >
                  {addon.isSelected ? "Added" : "+ Add"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="tw-text-3xl tw-text-blue tw-font-bold tw-my-12 tw-text-center">
          Choose your next step
        </div>
        <div>
          <div className="tw-bg-[#ECF0F4] tw-h-8"></div>
          <div className="md:tw-px-20 tw-px-4 md:tw-pb-14 md:tw-pt-12 tw-py-10 tw-bg-white tw-border tw-border-[#ECF0F4] tw-rounded">
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-gap-x-24 tw-gap-y-6">
              {selectedOption.loans && (
                <button
                  className="tw-border tw-border-orange tw-font-semibold tw-text-orange tw-rounded-full tw-justify-center tw-px-10 tw-py-4 tw-flex tw-items-center tw-gap-x-2 tw-w-full"
                  onClick={handleFinanceit}
                  disabled={isFinanceitLoading || isLoading}
                >
                  {isFinanceitLoading && (
                    <span className="loader !tw-w-6 !tw-h-6 !tw-border-2 tw-mr-3" />
                  )}
                  Get Financing <ArrowIcon />
                </button>
              )}
              <button
                className={`tw-bg-orange tw-font-semibold tw-text-white tw-rounded-full tw-justify-center tw-px-10 tw-py-4 tw-flex tw-items-center tw-gap-x-2 tw-w-full ${
                  isLoading ? "tw-opacity-70" : ""
                }`}
                onClick={handleFinishWithSpecialist}
                disabled={isLoading || isFinanceitLoading}
              >
                {isLoading && (
                  <span className="loader !tw-w-6 !tw-h-6 !tw-border-2 tw-mr-3" />
                )}
                Finalize with a Representative <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="tw-text-center tw-text-[10px] tw-mt-5 tw-mb-2">
        * On approved credit by Financeit Canada Inc.{" "}
        <a
          className="tw-underline"
          href="https://1clickenergy.com/pages/terms-and-conditions"
          target="_blank"
        >
          Terms and Conditions
        </a>{" "}
        apply.
      </div>
      <Toaster />
    </div>
  );
};
